import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtInt, timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, Plus, Copy, Check, Trash2, Eye, EyeOff, TerminalSquare } from "lucide-react";

const OTLP_BASE = `${window.location.origin}/api`;

function buildSnippet(tokenValue) {
  return `$env:CLAUDE_CODE_ENABLE_TELEMETRY=1
$env:OTEL_METRICS_EXPORTER="otlp"
$env:OTEL_LOGS_EXPORTER="otlp"
$env:OTEL_EXPORTER_OTLP_PROTOCOL="http/json"
$env:OTEL_EXPORTER_OTLP_ENDPOINT="${OTLP_BASE}"
$env:OTEL_EXPORTER_OTLP_HEADERS="Authorization=Bearer ${tokenValue}"`;
}

export default function IngestTokensPanel() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [label, setLabel] = useState("");
  const [revealed, setRevealed] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [copiedSetupId, setCopiedSetupId] = useState(null);

  const { data: tokens = [] } = useQuery({
    queryKey: ["ingest-tokens"],
    queryFn: async () => (await api.get("/ingest-tokens")).data,
  });

  const create = useMutation({
    mutationFn: async (lbl) => (await api.post("/ingest-tokens", { label: lbl })).data,
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ["ingest-tokens"] });
      setLabel("");
      setRevealed((r) => ({ ...r, [t.id]: true }));
      toast.success(`Token "${t.label}" created`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create token"),
  });

  const revoke = useMutation({
    mutationFn: async (id) => (await api.delete(`/ingest-tokens/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ingest-tokens"] });
      toast.success("Token revoked");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to revoke token"),
  });

  const copy = async (t) => {
    try {
      await navigator.clipboard.writeText(t.token);
      setCopiedId(t.id);
      toast.success("Token copied");
      setTimeout(() => setCopiedId(null), 1500);
    } catch (_) {
      toast.error("Copy failed — select and copy the token manually");
    }
  };

  const copySetup = async (id, tokenValue) => {
    try {
      await navigator.clipboard.writeText(buildSnippet(tokenValue));
      setCopiedSetupId(id);
      toast.success("Setup snippet copied — paste it on the developer's machine");
      setTimeout(() => setCopiedSetupId(null), 1500);
    } catch (_) {
      toast.error("Copy failed — copy the snippet from the Connect Claude Code panel");
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (label.trim()) create.mutate(label.trim());
  };

  const mask = (tok) => tok.slice(0, 6) + "•".repeat(14) + tok.slice(-4);

  return (
    <div className="bg-card border border-border" data-testid="ingest-tokens-panel">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-white" />
          <div>
            <p className="overline">ingest tokens</p>
            <h3 className="text-sm font-medium mt-1">Per-developer bearer tokens for traceability</h3>
          </div>
        </div>
        {isAdmin && (
          <form onSubmit={submit} className="flex items-center gap-2">
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. alice-macbook"
              data-testid="new-token-label-input"
              className="h-9 w-[200px] rounded-none bg-black/50 border-border font-mono text-xs focus:ring-1 focus:ring-white"
            />
            <Button
              type="submit"
              disabled={create.isPending || !label.trim()}
              data-testid="create-token-button"
              className="h-9 rounded-none bg-white text-black hover:bg-white/90 font-medium gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> New token
            </Button>
          </form>
        )}
        {!isAdmin && (
          <span className="font-mono text-[11px] text-muted-foreground" data-testid="tokens-readonly-note">
            read-only · showing only tokens assigned to you
          </span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full font-mono text-xs">
          <thead>
            <tr className="text-muted-foreground border-b border-border">
              {["Label", "Token", "Requests", "Last used", "Status", "Setup", ""].map((h) => (
                <th key={h} className="text-left font-normal px-3 py-2 whitespace-nowrap overline">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isAdmin && (
            <tr className="border-b border-border/50">
              <td className="px-3 py-2.5 text-white">default (env)</td>
              <td className="px-3 py-2.5 text-muted-foreground">configured via INGEST_TOKEN</td>
              <td className="px-3 py-2.5 text-muted-foreground">—</td>
              <td className="px-3 py-2.5 text-muted-foreground">—</td>
              <td className="px-3 py-2.5"><span className="text-green-500">active</span></td>
              <td className="px-3 py-2.5">
                <button
                  onClick={() => copySetup("env", "<INGEST_TOKEN>")}
                  data-testid="copy-setup-env"
                  className="flex items-center gap-1.5 text-muted-foreground hover:text-white transition-colors"
                  title="Copy Claude Code setup snippet (uses the env INGEST_TOKEN placeholder)"
                >
                  {copiedSetupId === "env" ? <Check className="w-3.5 h-3.5 text-green-500" /> : <TerminalSquare className="w-3.5 h-3.5" />}
                  copy setup
                </button>
              </td>
              <td></td>
            </tr>
            )}
            {tokens.map((t) => (
              <tr key={t.id} className="border-b border-border/50 hover:bg-white/[0.03] transition-colors" data-testid={`token-row-${t.id}`}>
                <td className="px-3 py-2.5 text-white">{t.label}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-300">{revealed[t.id] ? t.token : mask(t.token)}</span>
                    <button onClick={() => setRevealed((r) => ({ ...r, [t.id]: !r[t.id] }))} data-testid={`reveal-token-${t.id}`} className="text-muted-foreground hover:text-white">
                      {revealed[t.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => copy(t)} data-testid={`copy-token-${t.id}`} className="text-muted-foreground hover:text-white">
                      {copiedId === t.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-muted-foreground tabular-nums">{fmtInt(t.request_count)}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{t.last_used_at ? timeAgo(t.last_used_at) : "never"}</td>
                <td className="px-3 py-2.5">
                  <span className={t.active ? "text-green-500" : "text-red-500"}>{t.active ? "active" : "revoked"}</span>
                </td>
                <td className="px-3 py-2.5">
                  {t.active && (
                    <button
                      onClick={() => copySetup(t.id, t.token)}
                      data-testid={`copy-setup-${t.id}`}
                      className="flex items-center gap-1.5 text-muted-foreground hover:text-white transition-colors"
                      title="Copy Claude Code setup snippet with this token"
                    >
                      {copiedSetupId === t.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <TerminalSquare className="w-3.5 h-3.5" />}
                      copy setup
                    </button>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {isAdmin && t.active && (
                    <button onClick={() => revoke.mutate(t.id)} data-testid={`revoke-token-${t.id}`} className="text-muted-foreground hover:text-red-500 transition-colors" title="Revoke token">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-5 py-3 font-mono text-[11px] text-muted-foreground/70 border-t border-border">
        Give each developer/machine its own token, then filter the dashboard by token to trace where telemetry came from. Revoked tokens stop being accepted immediately.
      </p>
    </div>
  );
}
